import React, {createContext, useContext, useState} from 'react';
import {CountMeasurement, CountMeasurementItem} from "../models/editor";

export interface ExpandedAssetSlot {
    item: CountMeasurementItem;
    itemIndex: number;
    assetId: number | undefined;
}

function getExpandedSlots(countGroup: CountMeasurement): ExpandedAssetSlot[] {
    return countGroup.countItems.map((item, itemIndex) => ({
        item,
        itemIndex,
        assetId: item.assetId,
    }));
}

function getTotalSlotCount(countGroup: CountMeasurement): number {
    return countGroup.countItems.length;
}

interface AssetAssignmentContextType {
    isAssigning: string;
    customFieldValues: Record<string, any>;
    currentSlotIndex: number;
    setCurrentSlotIndex: (index: number) => void;
    setIsAssigning: (value: string) => void;
    setCustomFieldValues: (values: Record<string, any>) => void;
    hasNext: (value: CountMeasurement) => boolean;
    hasPrevious: () => boolean;
    fromBulk: boolean;
    isFromBulk: (fromBulk: boolean) => void;
    setNextUnassignedAsset: (value: CountMeasurement) => void;
    getCurrentSlot: (value: CountMeasurement | undefined) => ExpandedAssetSlot | null;
    setGroupFromBulkContinue: (value: CountMeasurement) => void;
    checkAllAssetAssigned: (value: CountMeasurement) => boolean;
    getTotalSlots: (value: CountMeasurement) => number;
    getExpandedSlots: (value: CountMeasurement) => ExpandedAssetSlot[];
    getCurrentItem: (value: CountMeasurement | undefined) => CountMeasurementItem | null;
}

const AssetAssignmentContext = createContext<AssetAssignmentContextType | null>(null);

export const AssetAssignmentProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [currentSlotIndex, setCurrentSlotIndex] = useState(0);
    const [isAssigningState, setIsAssigningState] = useState('');
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
    const [fromBulk, isFromBulk] = useState(false);

    const isAssigning = isAssigningState;
    const setIsAssigning = (value: string) => {
        setIsAssigningState(value);
        if (!value) {
            isFromBulk(false);
        }
    };

    const hasNext = (countGroup: CountMeasurement) => {
        const totalSlots = getTotalSlotCount(countGroup);
        return currentSlotIndex < totalSlots - 1;
    };

    const hasPrevious = () => currentSlotIndex > 0;

    const getCurrentSlot = (countGroup: CountMeasurement | undefined): ExpandedAssetSlot | null => {
        if (!countGroup || countGroup.id !== isAssigning) {
            return null;
        }
        const slots = getExpandedSlots(countGroup);
        return slots[currentSlotIndex] || null;
    };

    const getCurrentItem = (countGroup: CountMeasurement | undefined): CountMeasurementItem | null => {
        const slot = getCurrentSlot(countGroup);
        return slot?.item || null;
    };

    const setGroupFromBulkContinue = (countGroup: CountMeasurement) => {
        checkAssignedSlots(countGroup, 0);
        setIsAssigning(countGroup?.id || '');
        isFromBulk(true);
    };

    const setNextUnassignedAsset = (countGroup: CountMeasurement) => {
        checkAssignedSlots(countGroup, currentSlotIndex + 1);
    };

    const checkAssignedSlots = (countGroup: CountMeasurement, nextIndex: number) => {
        const slots = getExpandedSlots(countGroup);

        while (nextIndex < slots.length && slots[nextIndex].assetId) {
            nextIndex++;
        }

        if (nextIndex < slots.length) {
            setCurrentSlotIndex(nextIndex);
        } else {
            setIsAssigning('');
        }
    };

    const checkAllAssetAssigned = (countGroup: CountMeasurement): boolean => {
        const slots = getExpandedSlots(countGroup);
        return slots.every((slot) => !!slot.assetId);
    };

    const getTotalSlots = (countGroup: CountMeasurement): number => {
        return getTotalSlotCount(countGroup);
    };

    return (
        <AssetAssignmentContext.Provider
            value={{
                isAssigning,
                customFieldValues,
                fromBulk,
                currentSlotIndex,
                setCurrentSlotIndex,
                setIsAssigning,
                setCustomFieldValues,
                hasNext,
                hasPrevious,
                isFromBulk,
                setNextUnassignedAsset,
                getCurrentSlot,
                setGroupFromBulkContinue,
                checkAllAssetAssigned,
                getTotalSlots,
                getExpandedSlots,
                getCurrentItem,
            }}
        >
            {children}
        </AssetAssignmentContext.Provider>
    );
};

export const useAssetAssignment = () => {
    const context = useContext(AssetAssignmentContext);
    if (!context) {
        throw new Error('useAssetAssignment must be used within AssetAssignmentProvider');
    }
    return context;
};
